import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountdownTimer } from '../../../../../../hooks';
import { notify } from '../../../../../../service/pnotifyService';
import { CREDIT_CARD_STATUS, LOCAL_STORAGE_WEBHOOK_ID } from './constants';

const DEFAULT_SECONDS = 5;

const useAsyncBindingCardStatus = (pollingEndpoint, getCreditCardList) => {
  const { t } = useTranslation();
  const { hasFinished, isRunning, timeLeft, reset, start } = useCountdownTimer({
    defaultSeconds: DEFAULT_SECONDS,
  });
  const webhookMapRef = useRef(
    JSON.parse(localStorage.getItem(LOCAL_STORAGE_WEBHOOK_ID)) || {},
  );

  const getCreditCardStatus = useCallback(
    async (id) => {
      try {
        // because of using $http will happen cors error so change to use fetch
        const pollingResult = await fetch(`${pollingEndpoint}${id}`);
        const resultText = await pollingResult.text();

        if (
          ![CREDIT_CARD_STATUS.SUCCESS, CREDIT_CARD_STATUS.FAIL].includes(
            resultText,
          )
        ) {
          return;
        }

        webhookMapRef.current[id] = { isGetRes: true };
        await getCreditCardList();
        notify(
          ...(resultText === CREDIT_CARD_STATUS.SUCCESS
            ? [t('user.add_credit_card.success'), {}]
            : [
                t('user.add_credit_card.fail'),
                {
                  customClass: 'error',
                  icon: 'fa fa-exclamation-triangle',
                },
              ]),
        );
        localStorage.setItem(
          LOCAL_STORAGE_WEBHOOK_ID,
          JSON.stringify(webhookMapRef.current),
        );
      } catch (e) {
        console.error(e);
        notify(t('user.add_credit_card.fail'), {
          customClass: 'error',
          icon: 'fa fa-exclamation-triangle',
        });
      }
    },
    [getCreditCardList, pollingEndpoint, t],
  );

  const checkPollingStatus = useCallback(() => {
    Object.entries(webhookMapRef.current).forEach(([id, value]) => {
      if (!value.isGetRes) {
        getCreditCardStatus(id);
      }
    });
  }, [getCreditCardStatus]);

  useEffect(() => {
    if (isRunning || !hasFinished) {
      return;
    }

    const isAllGetRes = Object.values(webhookMapRef.current).every(
      (value) => value.isGetRes,
    );
    if (isAllGetRes) {
      reset();
    } else {
      checkPollingStatus();
      start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, isRunning, hasFinished]);

  return {
    startPolling: start,
    webhookMapRef,
  };
};

export default useAsyncBindingCardStatus;
